// #283 — hover / keyboard-focus preview for a "Recent events" row on the home page: the thumbnail
// bigger plus the basic details. The row's click is untouched (it opens the event popup); the card
// never takes the focus, and it is gone the moment the row is clicked or blurred.
// #289 — the one hover standard (src/lib/hoverIntent.ts): 100 ms first open, instant while warm,
// 150 ms grace so the pointer can move onto the card, focus opens at once, Esc closes, one card on
// screen at a time. The card has the shared hover look with an arrow at its row, the row is marked
// while its card is open, and a click on the card opens the event like the row's click.
import { useId, useRef, type FocusEvent, type PointerEvent, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { Activity } from 'lucide-react'
import { Popover, PopoverAnchor, HoverCardContent } from '@/components/ui/popover'
import type { Event } from '@/types'
import { formatCardDateTime } from '@/lib/utils'
import { useHoverIntent } from '@/lib/hoverIntent'
import { useEventTitle } from '@/hooks/useEventTitle'

export interface RecentEventPreviewRowProps {
  onPointerEnter: (e: PointerEvent<HTMLElement>) => void
  onPointerLeave: () => void
  onPointerDown: () => void
  onFocus: (e: FocusEvent<HTMLElement>) => void
  onBlur: (e: FocusEvent<HTMLElement>) => void
  onClick: () => void
  'aria-describedby'?: string
  /** #289 — present while the row's card is open (the row is marked) */
  'data-preview-open'?: ''
}

interface Props {
  event: Event
  /** #289 — a click on the card: open the event, as the row's click does */
  onActivate?: () => void
  /** Renders the row; spread `row` onto the row's button (merge your own onClick after it). */
  children: (row: RecentEventPreviewRowProps) => ReactElement
}

export function RecentEventPreview({ event, onActivate, children }: Props) {
  const { t } = useTranslation('cameras')
  const title = useEventTitle()
  const previewId = useId()
  const hover = useHoverIntent()
  // A mouse press focuses the row too; that focus must not open the card (the pointer path does).
  const viaPointer = useRef(false)
  // After a click the popup takes the focus and gives it back on close; that programmatic focus
  // must not pop the card open under a pointer that may be somewhere else.
  const suppressNextFocus = useRef(false)
  // #289 review — a press on the card moves the focus from the row into it; that is not a blur that ends the hover
  const cardRef = useRef<HTMLDivElement>(null)

  const row: RecentEventPreviewRowProps = {
    onPointerEnter: (e) => { if (e.pointerType === 'touch') return; hover.enter() },
    onPointerLeave: hover.leave,
    onPointerDown: () => { viaPointer.current = true },
    onFocus: () => {
      if (viaPointer.current || suppressNextFocus.current) {
        viaPointer.current = false
        suppressNextFocus.current = false
        return
      }
      hover.openNow()
    },
    onBlur: (e) => {
      if (e.relatedTarget instanceof Node && cardRef.current?.contains(e.relatedTarget)) return
      hover.close()
    },
    onClick: () => { suppressNextFocus.current = true; hover.close() },
    ...(hover.open ? { 'aria-describedby': previewId, 'data-preview-open': '' as const } : {}),
  }

  // #294 — a critical event: the critical title, as on its row
  const typeLabel = title(event)

  return (
    <Popover open={hover.open} onOpenChange={(next) => { if (!next) hover.close() }}>
      <PopoverAnchor asChild>{children(row)}</PopoverAnchor>
      <HoverCardContent
        ref={cardRef}
        id={previewId}
        role="tooltip"
        data-testid="recent-event-preview"
        instant={hover.instant}
        side="right"
        align="start"
        sideOffset={10}
        collisionPadding={8}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onPointerEnter={hover.enter}
        onPointerLeave={hover.leave}
        onClick={() => { hover.close(); onActivate?.() }}
        className="w-auto max-w-[340px] cursor-pointer p-3"
      >
        {/* the spacing lives here: on the card it would also push the arrow, which is a child too */}
        <div className="space-y-2">
          {event.thumbnail_url ? (
            <img
              src={event.thumbnail_url}
              alt={typeLabel}
              className="block h-auto max-h-[220px] w-[320px] max-w-full rounded-md bg-muted object-contain"
            />
          ) : (
            <div
              data-testid="preview-placeholder"
              className="flex h-40 w-[320px] max-w-full items-center justify-center rounded-md bg-amber-500/10"
            >
              <Activity className="h-8 w-8 text-amber-600 dark:text-amber-400" aria-hidden />
            </div>
          )}
          <div className="space-y-0.5">
            <p className="text-sm font-medium leading-tight">{typeLabel}</p>
            <p className="text-xs text-muted-foreground leading-tight">{event.camera_name}</p>
            <p className="text-xs text-muted-foreground leading-tight">{formatCardDateTime(event.occurred_at)}</p>
            {event.activity_caption && (
              <p data-testid="preview-caption" className="pt-1 text-xs leading-snug line-clamp-3">{event.activity_caption}</p>
            )}
            <p className="pt-1 text-[10px] text-muted-foreground/70">{t('dashboard.preview.hint')}</p>
          </div>
        </div>
      </HoverCardContent>
    </Popover>
  )
}
